import { useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Gift, MessageCircle, ArrowRight, Sparkles, CheckCircle2, Phone } from "lucide-react";

/**
 * Welcome dialog shown right after a passenger signs up.
 *
 * Behavior:
 *  - If the backend successfully pushed the WhatsApp message (provider != wa_me_link
 *    and delivery.delivered === true), we display a "Message envoyé" green badge.
 *  - Otherwise we fall back to the wa.me link which opens an EMPTY chat with the
 *    Trigo brand number, letting the user start the conversation themselves.
 *  - We also show a preview of the welcome text so the user knows what to expect.
 */
export default function WelcomePromoDialog({ promo, onClose }) {
  useEffect(() => {
    // No auto-open of WhatsApp anymore: the message is supposed to arrive
    // automatically in the client's WhatsApp. If our provider couldn't push it,
    // the user can tap the explicit button below to reach out manually.
  }, [promo]);

  if (!promo) return null;

  const delivered = promo.delivered === true;
  const brandPhonePretty = promo.brand_phone || "+237 670 738 113";
  const brandPhoneDigits = (promo.brand_phone || "+237670738113").replace(/\D/g, "");
  const contactUrl = `https://wa.me/${brandPhoneDigits}`;

  return (
    <Dialog open={!!promo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md overflow-hidden p-0 border-0" data-testid="welcome-promo-dialog">
        <div className="relative">
          {/* Decorative gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#1E3A5F]" />
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#FACC15]/30 blur-3xl animate-pulse" />
          <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-[#10B981]/20 blur-3xl" />

          <div className="relative px-6 pt-7 pb-6 text-white">
            <DialogHeader className="space-y-2">
              <div className="inline-flex w-fit items-center gap-1.5 px-3 py-1 rounded-full bg-[#FACC15] text-[#0A2540] text-[11px] font-bold uppercase tracking-wide">
                <Sparkles className="w-3 h-3" /> Offre de bienvenue
              </div>
              <DialogTitle className="text-2xl text-white font-extrabold leading-tight" style={{ fontFamily: 'Outfit' }}>
                🎉 Bienvenue sur Trigo&nbsp;Tour&nbsp;!
              </DialogTitle>
              <DialogDescription className="text-white/80 text-sm">
                Votre compte a bien été créé. Profitez immédiatement de votre récompense&nbsp;:
              </DialogDescription>
            </DialogHeader>

            {/* Discount card */}
            <div className="mt-4 p-4 rounded-2xl bg-white/95 text-[#0A2540] border border-white/20">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#FACC15] flex items-center justify-center shrink-0">
                  <Gift className="w-6 h-6 text-[#0A2540]" />
                </div>
                <div>
                  <p className="text-3xl font-extrabold leading-none" style={{ fontFamily: 'Outfit' }}>
                    -{promo.discount_percent || 15}%
                  </p>
                  <p className="text-xs text-[#64748B] font-medium">sur votre 1<sup>ère</sup> location</p>
                </div>
              </div>
              <ul className="mt-3 space-y-1 text-[11px] text-[#64748B]">
                <li>✓ Appliquée automatiquement à la réservation</li>
                <li>✓ Durée minimale : {promo.min_hours || 2}h</li>
                <li>✓ Valable 30 jours · Non cumulable avec un autre code</li>
              </ul>
            </div>

            {/* WhatsApp delivery status */}
            <div className="mt-4 p-3 rounded-xl bg-white/10 border border-white/15" data-testid="welcome-whatsapp-status">
              {delivered ? (
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-white">Message envoyé sur votre WhatsApp ✅</p>
                    <p className="text-[11px] text-white/70 leading-snug">
                      Vous venez de recevoir un message de bienvenue depuis le <strong>{brandPhonePretty}</strong>. Répondez-y pour nous contacter.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2.5">
                  <MessageCircle className="w-5 h-5 text-[#25D366] shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-white">Restons en contact sur WhatsApp</p>
                    <p className="text-[11px] text-white/70 leading-snug">
                      Écrivez-nous au <strong>{brandPhonePretty}</strong> pour toute question. Notre équipe est disponible 7j/7.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 flex flex-col gap-2">
              {!delivered && (
                <a
                  href={contactUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-[#25D366] hover:bg-[#1FAE52] text-white font-semibold text-sm transition-colors"
                  data-testid="welcome-whatsapp-btn"
                >
                  <Phone className="w-4 h-4" /> Nous contacter sur WhatsApp
                </a>
              )}
              <Button
                onClick={onClose}
                className="h-10 rounded-xl bg-[#FACC15] hover:bg-[#EAB308] text-[#0A2540] font-semibold text-sm"
                data-testid="welcome-explore-btn"
              >
                Explorer la plateforme <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
